import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useConnectivityStatus } from '../hooks/useConnectivityStatus.ts';
import './Login.css';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { isOnline, isBackendOnline } = useConnectivityStatus();
  const backendAvailable = isOnline && isBackendOnline;

  // Redirect if already authenticated (apenas se realmente autenticado)
  useEffect(() => {
    console.log('[Login] useEffect - isAuthenticated:', isAuthenticated);
    // Verificar também se há token no localStorage para evitar redirecionamentos indevidos
    const hasToken = localStorage.getItem('token');
    if (isAuthenticated && hasToken) {
      console.log('[Login] Usuário já autenticado, redirecionando para dashboard');
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevenir propagação do evento
    
    // Limpar erro anterior
    setError('');
    setLoading(true);
    console.log('[Login] handleSubmit chamado');

    try {
      console.log('[Login] Chamando função login...');
      const result = await login(email, password);
      console.log('[Login] Login bem-sucedido, resultado:', result);
      console.log('[Login] isAuthenticated após login:', isAuthenticated);
      console.log('[Login] Token no localStorage:', !!localStorage.getItem('token'));
      
      // Small delay to ensure state propagation
      setTimeout(() => {
        console.log('[Login] Verificando isAuthenticated após delay:', isAuthenticated);
        if (isAuthenticated || localStorage.getItem('token')) {
          console.log('[Login] Navegando para dashboard...');
          navigate('/dashboard', { replace: true });
        } else {
          console.error('[Login] ERRO: Token não encontrado após login!');
        }
      }, 200);
    } catch (err: any) {
      console.error('[Login] Erro no login:', err);
      
      // Extrair mensagem de erro de forma mais robusta
      let errorMessage = 'Erro ao fazer login. Verifique suas credenciais.';
      
      if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err?.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setLoading(false);
      
      // Garantir que o formulário não seja submetido novamente
      return false;
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="logo-container">
            <img 
              src="/logo.png"
              alt="Prumo" 
              className="login-logo"
              onError={(e) => {
                console.error('Erro ao carregar logo na página de login:', e);
                console.error('Tentou carregar de: /logo.png');
                const target = e.target as HTMLImageElement;
                if (target) {
                  target.style.display = 'none';
                }
              }}
              onLoad={() => {
                console.log('Logo carregado com sucesso na página de login');
              }}
            />
            <h1 className="logo-text">Prumo</h1>
          </div>
          <p className="slogan">Equilíbrio, Alinhamento e Precisão na organização das finanças e tarefas.</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form" noValidate>
          {!backendAvailable && (
            <div className="error-message" role="alert" aria-live="polite">
              Conexão com o backend necessária para login.
            </div>
          )}
          {error && (
            <div className="error-message" role="alert" aria-live="polite">
              {error}
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Digite seu email"
            />
          </div>

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <label htmlFor="password">Senha</label>
              <Link to="/forgot-password" className="forgot-password-link">
                Esqueceu sua senha?
              </Link>
            </div>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Digite sua senha"
            />
          </div>

          <button 
            type="submit" 
            className="login-button"
            disabled={loading || !backendAvailable}
          >
            {loading ? 'Entrando...' : 'Entrar'}
          </button>
        </form>

        <div className="login-footer">
          <p>
            Não tem uma conta?{' '}
            <Link to="/register" className="link">
              Cadastre-se aqui
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;